import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { salesService } from '../services/api';
import api from '../services/api';
import { formatDate, formatDateOnly, formatDateTime, formatPhone, formatCPF, formatDocumento, maskNameForList, maskDocumentForList, maskPhoneForList } from '../utils/masks';
import { useAuth } from '../contexts/AuthContext';
import '../styles/FilaTratamento.css';

// Obter URL base da API a partir da configuração do axios
const getApiBaseUrl = () => {
  // Usar a mesma baseURL configurada no axios
  return api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://212.47.74.155:9000';
};

// Data de hoje em Brasília no formato YYYY-MM-DD (para data_instalacao)
const getTodayBrasilia = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
};

const ORIGEM_VISUALIZACAO_LABEL = {
  vendas: 'Página Vendas (detalhes)',
  fila_tratamento: 'Fila — Modal Tratar venda',
  fila_historico: 'Fila — Modal Detalhes',
  vendas_editar: 'Página Vendas (editar)'
};

const FilaTratamento = () => {
  const { user } = useAuth();
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('pendente_bko');
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [tratamentoData, setTratamentoData] = useState({
    status: '',
    observacao_tratamento: '',
    data_instalacao: ''
  });
  const [updating, setUpdating] = useState(false);
  const [vendasAnteriores, setVendasAnteriores] = useState(null); // { telefone, cpf, email } -> [venda, ...]
  const [buscandoVendasAnteriores, setBuscandoVendasAnteriores] = useState(false);
  const [vendaParaMostrarHistorico, setVendaParaMostrarHistorico] = useState(null);
  const [vendaSelecionadaParaModal, setVendaSelecionadaParaModal] = useState(null); // Modal completo de detalhes
  const [histVizOpen, setHistVizOpen] = useState(false);
  const [histVizLoading, setHistVizLoading] = useState(false);
  const [histVizItems, setHistVizItems] = useState([]);

  useEffect(() => {
    loadFila();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  // Atualização automática da fila a cada 10 segundos
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Só atualiza se não houver modal aberto e não estiver carregando
      if (!selectedVenda && !loading && !updating) {
        loadFilaSilencioso();
      }
    }, 10000); // 10 segundos

    // Limpar interval quando o componente desmontar ou quando o filtro mudar
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, selectedVenda, loading, updating]);

  // Função para carregar fila silenciosamente (sem mostrar loading)
  const loadFilaSilencioso = async () => {
    try {
      const filters = { 
        status: filtroStatus === 'all' ? 'all' : filtroStatus 
      };
      const data = await salesService.getQueue(filters);
      setVendas(data);
    } catch (error) {
      console.error('Erro ao atualizar fila:', error);
      // Não mostrar alerta em atualizações silenciosas
    }
  };

  const loadFila = async () => {
    try {
      setLoading(true);
      const filters = { 
        status: filtroStatus === 'all' ? 'all' : filtroStatus 
      };
      const data = await salesService.getQueue(filters);
      setVendas(data);
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
      alert('Erro ao carregar fila de tratamento');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      'pendente_bko': 'status-pendente-bko',
      'tratando': 'status-tratando',
      'pendente_vendedor': 'status-pendente-vendedor',
      'pendente_sistemica': 'status-pendente-sistemica',
      'pendente_biometria': 'status-pendente-biometria',
      'pendente_auditoria': 'status-pendente-auditoria',
      'pendente_imput': 'status-pendente-imput',
      'aguardando_instalacao': 'status-aguardando-instalacao',
      'concluido': 'status-concluido',
      'cancelado': 'status-cancelado'
    };
    return classes[status] || '';
  };

  const getStatusText = (status) => {
    const texts = {
      'pendente_bko': 'Pendente BKO',
      'tratando': 'Tratando',
      'pendente_vendedor': 'Pendente Vendedor',
      'pendente_sistemica': 'Pendente Sistêmica',
      'pendente_biometria': 'Pendente Biometria',
      'pendente_auditoria': 'Pendente Auditoria',
      'pendente_imput': 'Pendente Imput',
      'aguardando_instalacao': 'Aguardando Instalação',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado'
    };
    return texts[status] || status;
  };

  // Função para calcular tempo de espera
  const calcularTempoEspera = (statusEnteredAt) => {
    if (!statusEnteredAt) return '-';
    
    const agora = new Date();
    const entrada = new Date(statusEnteredAt);
    const diffMs = agora - entrada;
    const diffMinutos = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMinutos / 60);
    const diffDias = Math.floor(diffHoras / 24);
    
    if (diffDias > 0) {
      return `${diffDias}d ${diffHoras % 24}h`;
    } else if (diffHoras > 0) {
      return `${diffHoras}h ${diffMinutos % 60}min`;
    } else {
      return `${diffMinutos}min`;
    }
  };

  const openTratamento = async (venda) => {
    try {
      // Verificar se é backoffice e se já está tratando outra venda
      if (user?.role === 'backoffice' && venda.status !== 'tratando') {
        // Verificar se há outra venda em "tratando" atribuída ao usuário atual
        const vendaEmTratamento = vendas.find(
          v => v.status === 'tratando' && 
               v.tratador?.id === user.id && 
               v.id !== venda.id
        );

        if (vendaEmTratamento) {
          alert(`⚠️ Você já está tratando a venda #${vendaEmTratamento.id}. Finalize o tratamento da venda atual antes de iniciar outro.`);
          // Mudar o filtro para mostrar a venda em tratamento
          setFiltroStatus('tratando');
          return;
        }
      }

      // Mudar status automaticamente para "tratando" quando abrir o modal
      // mas apenas se não estiver já em "tratando" ou em um status final
      if (venda.status !== 'tratando' && 
          venda.status !== 'concluido' && 
          venda.status !== 'cancelado') {
        try {
          await salesService.updateStatus(venda.id, 'tratando', '');
        } catch (error) {
          // Se o erro for que já está tratando outra venda, mostrar mensagem
          if (error.response?.status === 403 && error.response?.data?.error) {
            alert(`⚠️ ${error.response.data.error}`);
            if (error.response.data.venda_id) {
              // Mudar o filtro para mostrar a venda em tratamento
              setFiltroStatus('tratando');
            }
            return;
          }
          throw error;
        }
      }
      
      // Carregar dados completos da venda (com status atualizado)
      const vendaCompleta = await salesService.getById(venda.id);
      setSelectedVenda(vendaCompleta);
      const dataInstalacaoInicial = vendaCompleta.data_instalacao
        || vendaCompleta.data_agendamento1
        || getTodayBrasilia();
      setTratamentoData({
        status: vendaCompleta.status,
        observacao_tratamento: '', // Campo separado para nova observação adicional
        observacao_historico: vendaCompleta.observacao_tratamento || '', // Histórico completo (somente leitura)
        data_instalacao: dataInstalacaoInicial
      });
      salesService.registrarVisualizacao(venda.id, 'fila_tratamento').catch(() => {});
      
      // Se o filtro atual não inclui "tratando", mudar para "tratando" ou "all"
      // para que a venda permaneça visível na lista
      if (filtroStatus !== 'tratando' && filtroStatus !== 'all' && 
          vendaCompleta.status === 'tratando') {
        setFiltroStatus('tratando');
      }
    } catch (error) {
      console.error('Erro ao carregar dados da venda:', error);
      if (error.response?.data?.error) {
        alert(`❌ ${error.response.data.error}`);
      } else {
        alert('Erro ao carregar dados da venda');
      }
    }
  };

  const closeTratamento = () => {
    // Se a venda estava em "tratando", não recarregar a fila
    // Isso permite que o usuário reassuma o tratamento se fechar o modal por acidente
    const estavaTratando = selectedVenda?.status === 'tratando';
    setSelectedVenda(null);
    setTratamentoData({ status: '', observacao_tratamento: '', data_instalacao: '' });
    setVendasAnteriores(null);
    setVendaParaMostrarHistorico(null);
    setVendaSelecionadaParaModal(null);
    
    // Se estava tratando, mudar o filtro para "tratando" para manter a venda visível
    if (estavaTratando && filtroStatus !== 'tratando' && filtroStatus !== 'all') {
      setFiltroStatus('tratando');
    }
  };

  // Buscar vendas anteriores quando abrir o modal
  useEffect(() => {
    if (selectedVenda) {
      buscarVendasAnteriores(selectedVenda);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenda?.id]);

  // Função para buscar vendas anteriores com mesmo telefone, CPF ou email
  const buscarVendasAnteriores = async (venda) => {
    setBuscandoVendasAnteriores(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Adicionar ID da venda atual para excluir
      params.append('vendaAtualId', venda.id);
      
      // Adicionar telefones se existirem
      if (venda.telefone) {
        params.append('telefone', venda.telefone);
      }
      if (venda.telefone2) {
        params.append('telefone2', venda.telefone2);
      }
      
      // Adicionar CPF se existir
      if (venda.cpf) {
        params.append('cpf', venda.cpf);
      } else if (venda.documento) {
        params.append('cpf', venda.documento); // documento pode ser CPF
      }
      
      // Adicionar email se existir
      if (venda.email) {
        params.append('email', venda.email);
      }
      
      const response = await fetch(`${api.defaults.baseURL}/api/sales/contatos-repetidos?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendasAnteriores(data);
      } else {
        console.warn('Não foi possível buscar vendas anteriores:', response.statusText);
        setVendasAnteriores({});
      }
    } catch (error) {
      console.error('Erro ao buscar vendas anteriores:', error);
      setVendasAnteriores({});
    } finally {
      setBuscandoVendasAnteriores(false);
    }
  };

  // Função auxiliar para verificar se um contato (telefone, CPF ou email) está repetido
  const verificarContatoRepetido = (tipo, valor) => {
    if (!valor || valor === '-' || !vendasAnteriores) return null;
    const lista = vendasAnteriores[tipo];
    if (!Array.isArray(lista) || lista.length === 0) return null;
    // API já retornou vendas que batem com o contato pesquisado - se há itens, está repetido
    return true;
  };

  // Função para abrir modal de histórico de uma venda específica
  const abrirHistoricoVenda = () => {
    setVendaParaMostrarHistorico(selectedVenda);
  };

  // Função para abrir modal de detalhes completo de uma venda
  const abrirModalDetalhesVenda = async (venda) => {
    try {
      const full = await salesService.getById(venda.id);
      setVendaSelecionadaParaModal(full);
      salesService.registrarVisualizacao(venda.id, 'fila_historico').catch(() => {});
    } catch (error) {
      console.error('Erro ao carregar detalhes da venda:', error);
      alert(error.response?.data?.error || 'Erro ao carregar detalhes da venda');
    }
  };

  // Função para fechar modal de histórico
  const fecharHistoricoVenda = () => {
    setVendaParaMostrarHistorico(null);
    setHistVizOpen(false);
    setHistVizItems([]);
    setVendaSelecionadaParaModal(null);
  };

  // Função para fechar modal de detalhes
  const fecharModalDetalhes = () => {
    setHistVizOpen(false);
    setHistVizItems([]);
    setVendaSelecionadaParaModal(null);
  };

  const abrirHistVisualizacaoFila = async () => {
    if (!vendaSelecionadaParaModal?.id) return;
    setHistVizOpen(true);
    setHistVizLoading(true);
    setHistVizItems([]);
    try {
      const data = await salesService.getHistoricoVisualizacao(vendaSelecionadaParaModal.id);
      setHistVizItems(Array.isArray(data) ? data : []);
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao carregar histórico de visualização');
      setHistVizItems([]);
    } finally {
      setHistVizLoading(false);
    }
  };

  // Componente de renderização para campos de contato com destaque (vermelho + lupa se repetido)
  const renderizarCampoContato = (label, valor, tipoContato, valorParaBuscar = null) => {
    if (!valor || valor === '-') {
      return (
        <div className="info-item">
          <strong>{label}:</strong>
          <span>{valor}</span>
        </div>
      );
    }

    // Se não tiver vendas anteriores carregadas, mostrar normal
    if (!vendasAnteriores || Object.keys(vendasAnteriores).length === 0) {
      return (
        <div className="info-item">
          <strong>{label}:</strong>
          <span>{valor}</span>
        </div>
      );
    }

    // Tentar buscar o valor específico se não foi passado
    const valorBusca = valorParaBuscar || valor;
    const repetido = verificarContatoRepetido(tipoContato, valorBusca);

    return (
      <div className={`info-item ${repetido ? 'info-item-contato-repetido' : ''}`}>
        <strong>{label}:</strong>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {valor}
          {repetido && (
            <button
              type="button"
              className="lupa-contato"
              onClick={() => abrirHistoricoVenda()}
              title="Ver vendas anteriores com este contato"
            >
              🔍
            </button>
          )}
        </span>
      </div>
    );
  };

  const handleTratamentoChange = (e) => {
    const { name, value } = e.target;
    setTratamentoData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'status' && value === 'aguardando_instalacao' && !prev.data_instalacao) {
        next.data_instalacao = selectedVenda?.data_agendamento1 || getTodayBrasilia();
      }
      return next;
    });
  };

  // Monta e copia a ficha de cadastro no formato solicitado
  const copiarFichaCadastro = () => {
    if (!selectedVenda) return;
    const v = selectedVenda;

    const separador = '━━━━━━━━━━━━━━━━━━';
    const linha = (label, valor) => `✔ ${label}: ${valor || ''}`;
    const linhaOpcional = (label, valor) => valor ? `✔ ${label}: ${valor}` : `✔ ${label}: `;
    const complemento = [v.complemento1, v.complemento2].filter(Boolean).join(', ') || '';
    const dataNasc = v.data_nascimento ? formatDateOnly(v.data_nascimento) : '';
    const periodo1 = v.periodo_agendamento1 === 'manha' ? 'Manhã' : 
                     v.periodo_agendamento1 === 'tarde' ? 'Tarde' : 
                     v.periodo_agendamento1 === 'noite' ? 'Noite' : '';
    const periodo2 = v.periodo_agendamento2 === 'manha' ? 'Manhã' : 
                     v.periodo_agendamento2 === 'tarde' ? 'Tarde' : 
                     v.periodo_agendamento2 === 'noite' ? 'Noite' : '';
    const dataAgendamento1 = v.data_agendamento1 ? `${formatDateOnly(v.data_agendamento1)}${periodo1 ? ' (' + periodo1 + ')' : ''}` : '';
    const dataAgendamento2 = v.data_agendamento2 ? `${formatDateOnly(v.data_agendamento2)}${periodo2 ? ' (' + periodo2 + ')' : ''}` : '';

    // Formatar forma de pagamento (baseado no campo forma_pagamento)
    const formaPagamento = v.forma_pagamento || '';
    const boletoChecked = formaPagamento.toLowerCase().includes('boleto') ? '✔' : '   ';
    const debitoChecked = formaPagamento.toLowerCase().includes('débito') || formaPagamento.toLowerCase().includes('debito') ? '✔' : '   ';

    let texto = `📋 FORMULÁRIO DE PEDIDO ✅

${separador}
👤 DADOS DO CLIENTE

${linha('NOME', (v.nome_cliente || '').toUpperCase())}
${linha('CPF', v.cpf ? formatCPF(v.cpf) : v.documento ? formatDocumento(v.documento) : '')}
${linha('DATA DE NASCIMENTO', dataNasc)}
${linha('RG', v.rg || '')}
${linha('NOME DA MÃE', (v.nome_mae || '').toUpperCase())}
${linha('WHATSAPP', v.telefone ? formatPhone(v.telefone) : '')}
${linha('FONE 1', v.telefone2 ? formatPhone(v.telefone2) : '')}
${linha('E-MAIL', (v.email || '').toUpperCase())}

${separador}
📍 ENDEREÇO

${linha('CEP', v.cep || '')}
${linha('RUA', v.logradouro || '')}
${linha('NÚMERO', v.numero || '')}
${linha('COMPLEMENTO', complemento)}
${linha('BAIRRO', (v.bairro || '').toUpperCase())}
${linha('CIDADE', (v.cidade || '').toUpperCase())}

${separador}
📦 DADOS DO PLANO

${linha('PLANO', v.plano_nome || v.descricao_plano || '')}
${linha('VENCIMENTO (VCTO)', v.data_vencimento || '')}
${linha('VALOR', v.valor_total ? parseFloat(v.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '')}
${linhaOpcional('FORMA DE PAGAMENTO:')}
BOLETO ( ${boletoChecked} )     DÉBITO C/C ( ${debitoChecked} )

${linhaOpcional('BANCO', v.banco || '')}
${linhaOpcional('AGÊNCIA', v.agencia || '')}
${linhaOpcional('CONTA', v.conta || '')}

${separador}
📅 DISPONIBILIDADE PARA INSTALAÇÃO

${linha('1ª DATA (com período)', dataAgendamento1)}
${linha('2ª DATA (com período)', dataAgendamento2)}

${separador}
🔁 EM CASO DE PORTABILIDADE

${linha('Nº PARA PORTABILIDADE', v.numero_portabilidade || '')}
${linha('OPERADORA ATUAL', v.operadora_atual || '')}`;

    navigator.clipboard.writeText(texto).then(() => {
      alert('✅ Ficha de cadastro copiada para a área de transferência!');
    }).catch(() => {
      alert('❌ Não foi possível copiar. Tente selecionar e copiar manualmente.');
    });
  };

  // Função para baixar Excel da venda Claro via backend
  const downloadExcelClaro = async () => {
    if (!selectedVenda) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${api.defaults.baseURL}/api/sales/${selectedVenda.id}/download-excel-claro`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao gerar Excel');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedVenda.nome_cliente || 'Cliente'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('✅ Excel baixado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao baixar Excel:', error);
      alert(`❌ ${error.message}`);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();

    if (!tratamentoData.status) {
      alert('Selecione um status');
      return;
    }
    if (tratamentoData.status === 'aguardando_instalacao' && !tratamentoData.data_instalacao?.trim()) {
      alert('Informe a data da instalação para o status "Aguardando Instalação".');
      return;
    }

    try {
      setUpdating(true);
      const novoStatus = tratamentoData.status;
      const dataInstalacao = novoStatus === 'aguardando_instalacao' && tratamentoData.data_instalacao
        ? tratamentoData.data_instalacao
        : undefined;
      await salesService.updateStatus(
        selectedVenda.id,
        novoStatus,
        tratamentoData.observacao_tratamento,
        dataInstalacao
      );
      
      alert('✅ Status atualizado com sucesso!');
      
      // Se o novo status não for "tratando" e o filtro atual for "tratando", 
      // mudar para o filtro adequado ou "all"
      if (novoStatus !== 'tratando' && filtroStatus === 'tratando') {
        setFiltroStatus(novoStatus);
      } else if (novoStatus === 'tratando' && filtroStatus !== 'tratando' && filtroStatus !== 'all') {
        setFiltroStatus('tratando');
      }
      
      closeTratamento();
      loadFila();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('❌ Erro ao atualizar status');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Layout>
      <div className="fila-page">
        <div className="page-header">
          <h1>📋 Fila de Tratamento</h1>
          <p>Gerencie as vendas que precisam de tratamento</p>
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="filter-select"
            >
              <option value="pendente_bko">Pendente BKO</option>
              <option value="tratando">Tratando</option>
              <option value="pendente_vendedor">Pendente Vendedor</option>
              <option value="pendente_sistemica">Pendente Sistêmica</option>
              <option value="pendente_biometria">Pendente Biometria</option>
              <option value="pendente_auditoria">Pendente Auditoria</option>
              <option value="pendente_imput">Pendente Imput</option>
              <option value="aguardando_instalacao">Aguardando Instalação</option>
              <option value="all">Todos (Pendentes)</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Carregando fila...</div>
        ) : vendas.length === 0 ? (
          <div className="empty-state">
            <p>📭 Nenhuma venda na fila de tratamento</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="fila-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Documento</th>
                  <th>Telefone</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Vendedor</th>
                  <th>BKO Atribuído</th>
                  <th>Operadora</th>
                  <th>Data Cadastro</th>
                  <th>Entrou na Fila</th>
                  <th>Tempo Aguardando</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vendas.map((venda) => (
                  <tr key={venda.id}>
                    <td>#{venda.id}</td>
                    <td>{maskNameForList((venda.tipo_cliente === 'cnpj' ? (venda.razao_social || venda.nome_cliente) : venda.nome_cliente) || '')}</td>
                    <td>{maskDocumentForList(venda.documento, venda.tipo_cliente)}</td>
                    <td>{maskPhoneForList(venda.telefone)}</td>
                    <td>
                      R$ {parseFloat(venda.valor_total).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>
                      {venda.status === 'aguardando_instalacao' && venda.data_instalacao && (
                        <small style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--texto-secundario)' }}>
                          {formatDateOnly(venda.data_instalacao)}
                        </small>
                      )}
                      <span className={`status-badge ${getStatusClass(venda.status)}`}>
                        {getStatusText(venda.status)}
                      </span>
                    </td>
                    <td>{venda.vendedor?.nome || '-'}</td>
                    <td>
                      {venda.tratador?.nome ? (
                        <span style={{ color: 'var(--azul-dvs)', fontWeight: '600' }}>
                          {venda.tratador.nome}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)', fontStyle: 'italic' }}>
                          Sem atribuição
                        </span>
                      )}
                    </td>
                    <td>
                      {venda.operadora_selecionada ? (
                        <span style={{ fontWeight: '600' }}>
                          {venda.operadora_selecionada.charAt(0).toUpperCase() + 
                           venda.operadora_selecionada.slice(1).toLowerCase()}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)' }}>-</span>
                      )}
                    </td>
                    <td>{formatDate(venda.created_at)}</td>
                    <td>
                      {venda.status_entered_at ? (
                        <div style={{ fontSize: '0.85rem' }}>
                          <div style={{ fontWeight: '600', color: 'var(--texto-primario)' }}>
                            {formatDateTime(venda.status_entered_at)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td>
                      {venda.status_entered_at ? (
                        <div style={{ fontSize: '0.85rem' }}>
                          {(() => {
                            const tempoEspera = calcularTempoEspera(venda.status_entered_at);
                            const agora = new Date();
                            const entrada = new Date(venda.status_entered_at);
                            const diffHoras = Math.floor((agora - entrada) / 3600000);
                            const diffDias = Math.floor(diffHoras / 24);
                            
                            let cor = 'var(--azul-dvs)';
                            if (diffDias > 0) {
                              cor = '#ff6b6b'; // Vermelho para mais de 1 dia
                            } else if (diffHoras >= 2) {
                              cor = '#ffa500'; // Laranja para 2+ horas
                            }
                            
                            return (
                              <div style={{ fontWeight: '600', color: cor }}>
                                {tempoEspera}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td>
                      <button 
                        onClick={() => openTratamento(venda)}
                        className="btn-action"
                      >
                        🔧 Tratar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal de tratamento */}
        {selectedVenda && (
          <div className="modal-overlay" onClick={closeTratamento}>
            <div className="modal-content tratamento-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🔧 Tratar Venda #{selectedVenda.id}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {selectedVenda.operadora_selecionada &&
                   selectedVenda.operadora_selecionada.toLowerCase() === 'claro' && (
                    <button
                      type="button"
                      onClick={downloadExcelClaro}
                      className="btn-primary"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', background: '#28a745' }}
                      title="Baixar checklist em Excel"
                    >
                      📥 Download Excel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={copiarFichaCadastro}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    📋 Copiar tudo
                  </button>
                  <button onClick={closeTratamento} className="btn-close">✕</button>
                </div>
              </div>
              
              <div className="modal-body">
                {/* Informações do Cliente */}
                <div className="venda-info">
                  <h3>👤 Dados do Cliente</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Tipo de Cliente:</strong>
                      <span>{selectedVenda.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Documento Principal:</strong>
                      <span>{formatDocumento(selectedVenda.documento)}</span>
                    </div>
                    <div className="info-item">
                      <strong>Nome do Cliente:</strong>
                      <span>{selectedVenda.nome_cliente || '-'}</span>
                    </div>
                    {selectedVenda.tipo_cliente === 'cnpj' && (
                      <>
                        <div className="info-item">
                          <strong>Razão Social:</strong>
                          <span>{selectedVenda.razao_social || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Estado do CNPJ:</strong>
                          <span>{selectedVenda.estado_cnpj || '-'}</span>
                        </div>
                        {selectedVenda.nome_representante && (
                          <div className="info-item">
                            <strong>Nome do Representante:</strong>
                            <span>{selectedVenda.nome_representante}</span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedVenda.tipo_cliente === 'cpf' && (
                      <>
                        <div className="info-item">
                          <strong>CPF:</strong>
                          <span>{selectedVenda.cpf ? formatCPF(selectedVenda.cpf) : '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>RG:</strong>
                          <span>{selectedVenda.rg || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Nome da Mãe:</strong>
                          <span>{selectedVenda.nome_mae || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Data de Nascimento:</strong>
                          <span>{selectedVenda.data_nascimento ? formatDateOnly(selectedVenda.data_nascimento) : '-'}</span>
                        </div>
                      </>
                    )}
                    {renderizarCampoContato('Email', selectedVenda.email || '-', 'email', selectedVenda.email)}
                    {renderizarCampoContato('Telefone 1', selectedVenda.telefone ? formatPhone(selectedVenda.telefone) : '-', 'telefone', selectedVenda.telefone)}
                    {renderizarCampoContato('Telefone 2', selectedVenda.telefone2 ? formatPhone(selectedVenda.telefone2) : '-', 'telefone', selectedVenda.telefone2)}
                  </div>
                </div>

                {/* Informações de Endereço */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>📍 Endereço</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>CEP:</strong>
                      <span>{selectedVenda.cep || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Logradouro:</strong>
                      <span>{selectedVenda.logradouro || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Número:</strong>
                      <span>{selectedVenda.numero || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Complemento 1:</strong>
                      <span>{selectedVenda.complemento1 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Complemento 2:</strong>
                      <span>{selectedVenda.complemento2 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Bairro:</strong>
                      <span>{selectedVenda.bairro || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Cidade:</strong>
                      <span>{selectedVenda.cidade || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Estado:</strong>
                      <span>{selectedVenda.estado || '-'}</span>
                    </div>
                    {selectedVenda.estado_completo && (
                      <div className="info-item">
                        <strong>Estado Completo:</strong>
                        <span>{selectedVenda.estado_completo}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações de Agendamento */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>📅 Agendamento</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Data de Agendamento 1:</strong>
                      <span>{selectedVenda.data_agendamento1 ? formatDateOnly(selectedVenda.data_agendamento1) : '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Período de Agendamento 1:</strong>
                      <span>
                        {selectedVenda.periodo_agendamento1 === 'manha' ? 'Manhã' : 
                         selectedVenda.periodo_agendamento1 === 'tarde' ? 'Tarde' : 
                         selectedVenda.periodo_agendamento1 === 'noite' ? 'Noite' : '-'}
                      </span>
                    </div>
                    {selectedVenda.data_agendamento2 && (
                      <>
                        <div className="info-item">
                          <strong>Data de Agendamento 2:</strong>
                          <span>{formatDateOnly(selectedVenda.data_agendamento2)}</span>
                        </div>
                        <div className="info-item">
                          <strong>Período de Agendamento 2:</strong>
                          <span>
                            {selectedVenda.periodo_agendamento2 === 'manha' ? 'Manhã' : 
                             selectedVenda.periodo_agendamento2 === 'tarde' ? 'Tarde' : 
                             selectedVenda.periodo_agendamento2 === 'noite' ? 'Noite' : '-'}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="info-item">
                      <strong>Aceita Antecipação:</strong>
                      <span>{selectedVenda.aceita_antecipacao ? '✅ Sim' : '❌ Não'}</span>
                    </div>
                    {selectedVenda.observacao_instalacao && (
                      <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                        <strong>Observação de Instalação:</strong>
                        <span>{selectedVenda.observacao_instalacao}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações Financeiras */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>💰 Informações Financeiras</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Valor Total:</strong>
                      <span className="value-highlight">
                        R$ {parseFloat(selectedVenda.valor_total || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </div>
                    <div className="info-item">
                      <strong>Data de Vencimento:</strong>
                      <span>Dia {selectedVenda.data_vencimento}</span>
                    </div>
                  </div>
                </div>

                {/* Informações da Venda */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>ℹ️ Informações da Venda</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Vendedor:</strong>
                      <span>{selectedVenda.vendedor?.nome || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Status:</strong>
                      {selectedVenda.status === 'aguardando_instalacao' && selectedVenda.data_instalacao && (
                        <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                          Data instalação: {formatDateOnly(selectedVenda.data_instalacao)}
                        </span>
                      )}
                      <span className={`status-badge ${getStatusClass(selectedVenda.status)}`}>
                        {getStatusText(selectedVenda.status)}
                      </span>
                    </div>
                    <div className="info-item">
                      <strong>Cadastrado em:</strong>
                      <span>{formatDateTime(selectedVenda.created_at)}</span>
                    </div>
                    {selectedVenda.tratado_por && (
                      <>
                        <div className="info-item">
                          <strong>Tratado por:</strong>
                          <span>{selectedVenda.tratador?.nome || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Tratado em:</strong>
                          <span>{selectedVenda.tratado_em ? formatDateTime(selectedVenda.tratado_em) : '-'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Informações de Viabilidade */}
                {selectedVenda.operadora_selecionada && (
                  <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                    <h3>📡 Informações de Viabilidade</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <strong>Operadora Selecionada:</strong>
                        <span style={{ fontWeight: '600' }}>
                          {selectedVenda.operadora_selecionada.charAt(0).toUpperCase() + selectedVenda.operadora_selecionada.slice(1)}
                        </span>
                      </div>
                      {selectedVenda.operadora_viabilidade_data && (
                        <div className="info-item">
                          <strong>Data da Consulta:</strong>
                          <span>{formatDateTime(selectedVenda.operadora_viabilidade_data)}</span>
                        </div>
                      )}
                      {selectedVenda.operadora_viabilidade_json && (
                        <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                          <strong>Status de Viabilidade:</strong>
                          <span>
                            {(() => {
                              try {
                                const viabilidade = typeof selectedVenda.operadora_viabilidade_json === 'string' 
                                  ? JSON.parse(selectedVenda.operadora_viabilidade_json)
                                  : selectedVenda.operadora_viabilidade_json;
                                const operadora = viabilidade.operadoras?.[selectedVenda.operadora_selecionada];
                                if (operadora) {
                                  const hasViabilidade = operadora.coverage === true;
                                  return (
                                    <span className={`status-badge ${hasViabilidade ? 'status-ativo' : 'status-inativo'}`} style={{ 
                                      background: hasViabilidade ? '#4caf50' : '#ff9800',
                                      color: 'white',
                                      padding: '0.25rem 0.75rem',
                                      borderRadius: '4px'
                                    }}>
                                      {hasViabilidade ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada - BKO consultará manualmente'}
                                    </span>
                                  );
                                }
                                return '-';
                              } catch (e) {
                                return '-';
                              }
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Observações */}
                {selectedVenda.observacao_venda && (
                  <div className="observacao-box" style={{ marginTop: '1.5rem' }}>
                    <strong>📝 Observação da Venda:</strong>
                    <p>{selectedVenda.observacao_venda}</p>
                  </div>
                )}

                {/* Histórico de Observações de Tratamento (somente leitura) */}
                {selectedVenda.observacao_tratamento && (
                  <div className="observacao-box" style={{ marginTop: '1rem', borderLeftColor: '#2196f3' }}>
                    <strong>📋 Histórico de Tratamento (somente leitura):</strong>
                    <textarea
                      readOnly
                      value={selectedVenda.observacao_tratamento}
                      style={{
                        width: '100%',
                        minHeight: '150px',
                        padding: '0.75rem',
                        marginTop: '0.5rem',
                        background: 'var(--cinza-escuro-2)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        color: 'var(--texto-primario)',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                        cursor: 'default'
                      }}
                    />
                  </div>
                )}

                {/* Documentos Anexos */}
                {selectedVenda.documento_anexo && (
                  <div className="observacao-box" style={{ marginTop: '1rem', borderLeftColor: '#ff9800' }}>
                    <strong>📎 Documentos Anexos:</strong>
                    <div style={{ marginTop: '0.75rem' }}>
                      {(() => {
                        // Tentar fazer parse do JSON (múltiplos arquivos) ou usar como string (arquivo único - compatibilidade)
                        let documentos = [];
                        try {
                          const parsed = JSON.parse(selectedVenda.documento_anexo);
                          documentos = Array.isArray(parsed) ? parsed : [parsed];
                        } catch (e) {
                          // Se não for JSON, tratar como string única (compatibilidade com dados antigos)
                          documentos = [selectedVenda.documento_anexo];
                        }
                        
                        // Função para construir URL correta do documento
                        const getDocumentoUrl = (documentoPath) => {
                          // Se o caminho já começa com http, retornar como está
                          if (documentoPath.startsWith('http://') || documentoPath.startsWith('https://')) {
                            return documentoPath;
                          }
                          
                          // Usar a mesma URL base da API configurada no axios
                          let baseUrl = getApiBaseUrl();
                          
                          // Remover /api do final se existir (os uploads são servidos na raiz do servidor)
                          baseUrl = baseUrl.replace(/\/api\/?$/, '');
                          
                          // Remover barra final se existir
                          baseUrl = baseUrl.replace(/\/$/, '');
                          
                          // Garantir que o caminho tenha o formato correto
                          // Se não começar com "uploads/", adicionar
                          let path = documentoPath;
                          if (!path.startsWith('uploads/') && !path.startsWith('/uploads/')) {
                            // Se tiver apenas o nome do arquivo, adicionar o caminho completo
                            if (!path.includes('/')) {
                              path = `uploads/documentos/${path}`;
                            }
                          }
                          
                          // Garantir que o caminho comece com /
                          path = path.startsWith('/') ? path : `/${path}`;
                          
                          const fullUrl = `${baseUrl}${path}`;
                          console.log('📂 Construindo URL do documento:', {
                            documentoPath,
                            baseUrl,
                            path,
                            fullUrl
                          });
                          return fullUrl;
                        };
                        
                        return (
                          <>
                            {documentos.map((documentoPath, index) => {
                              const documentoUrl = getDocumentoUrl(documentoPath);
                              const isImage = /\.(jpg|jpeg|png)$/i.test(documentoPath);
                              const isPdf = /\.pdf$/i.test(documentoPath);
                              const fileName = documentoPath.split('/').pop();
                              
                              // Função para fazer download
                              const handleDownload = async (e) => {
                                e.preventDefault();
                                console.log('📥 Tentando baixar:', documentoUrl);
                                
                                try {
                                  // Tentar usar fetch primeiro
                                  const response = await fetch(documentoUrl, {
                                    method: 'GET',
                                    headers: {
                                      'Accept': '*/*'
                                    }
                                  });
                                  
                                  if (!response.ok) {
                                    console.error('❌ Erro na resposta:', response.status, response.statusText);
                                    throw new Error(`Arquivo não encontrado (${response.status})`);
                                  }
                                  
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = fileName;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                  console.log('✅ Download iniciado com sucesso');
                                } catch (error) {
                                  console.error('❌ Erro ao baixar arquivo:', error);
                                  // Se fetch falhar, tentar abrir em nova aba como fallback
                                  alert(`⚠️ Não foi possível fazer o download automático. Abrindo em nova aba...\n\nErro: ${error.message}`);
                                  window.open(documentoUrl, '_blank');
                                }
                              };
                              
                              return (
                                <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(42, 42, 42, 0.3)', borderRadius: '8px' }}>
                                  <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: 'var(--texto-primario)' }}>
                                    📄 Documento {documentos.length > 1 ? `${index + 1}` : ''}: {fileName}
                                  </div>
                                  {isImage && (
                                    <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                      <img 
                                        src={documentoUrl}
                                        alt={`Documento anexo ${index + 1}`}
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '500px', 
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '8px',
                                          cursor: 'pointer',
                                          objectFit: 'contain',
                                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                          display: 'block'
                                        }}
                                        onClick={() => window.open(documentoUrl, '_blank')}
                                        onError={(e) => {
                                          // Se a imagem falhar ao carregar, mostrar mensagem
                                          const img = e.target;
                                          img.style.display = 'none';
                                          const parent = img.parentNode;
                                          // Verificar se já existe mensagem de erro
                                          if (!parent.querySelector('.image-error-message')) {
                                            const errorDiv = document.createElement('div');
                                            errorDiv.className = 'image-error-message';
                                            errorDiv.style.cssText = 'padding: 2rem; text-align: center; color: var(--texto-secundario); background: rgba(255, 152, 0, 0.1); border-radius: 8px; border: 1px solid rgba(255, 152, 0, 0.3);';
                                            errorDiv.innerHTML = `
                                              <div style="margin-bottom: 0.5rem;">⚠️ Imagem não pôde ser carregada</div>
                                              <div style="font-size: 0.85rem; opacity: 0.8;">Use os botões abaixo para abrir ou baixar o documento</div>
                                            `;
                                            parent.appendChild(errorDiv);
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                  {isPdf && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                      <iframe
                                        src={documentoUrl}
                                        style={{
                                          width: '100%',
                                          height: '600px',
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '8px'
                                        }}
                                        title={`Documento PDF ${index + 1}`}
                                        onError={() => {
                                          console.error('Erro ao carregar PDF');
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                                    <button
                                      onClick={() => window.open(documentoUrl, '_blank')}
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.5rem 1rem',
                                        background: 'var(--azul-dvs)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      🔗 Abrir em Nova Aba
                                    </button>
                                    <button
                                      onClick={handleDownload}
                                      type="button"
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.5rem 1rem',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      📥 Baixar Documento
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Formulário de tratamento */}
                <form onSubmit={handleUpdateStatus} className="tratamento-form">
                  <div className="form-group">
                    <label>Novo Status *</label>
                    <select
                      name="status"
                      value={tratamentoData.status}
                      onChange={handleTratamentoChange}
                      required
                    >
                      <option value="">Selecione o status</option>
                      <option value="pendente_bko">Pendente BKO</option>
                      <option value="tratando">Tratando</option>
                      <option value="pendente_vendedor">Pendente Vendedor</option>
                      <option value="pendente_sistemica">Pendente Sistêmica</option>
                      <option value="pendente_biometria">Pendente Biometria</option>
                      <option value="pendente_auditoria">Pendente Auditoria</option>
                      <option value="pendente_imput">Pendente Imput</option>
                      <option value="aguardando_instalacao">Aguardando Instalação</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>

                  {tratamentoData.status === 'aguardando_instalacao' && (
                    <div className="form-group">
                      <label>Data da Instalação *</label>
                      <input
                        type="date"
                        name="data_instalacao"
                        value={tratamentoData.data_instalacao || ''}
                        onChange={handleTratamentoChange}
                        required
                      />
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                        Data em que está agendada a instalação (usada no filtro da página Vendas).
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Observação Adicional (Opcional)</label>
                    <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginBottom: '0.5rem' }}>
                      Esta observação será adicionada ao histórico automaticamente. O histórico completo acima não pode ser editado.
                    </p>
                    <textarea
                      name="observacao_tratamento"
                      value={tratamentoData.observacao_tratamento}
                      onChange={handleTratamentoChange}
                      placeholder="Digite uma observação adicional sobre o tratamento desta venda (opcional)..."
                      rows="4"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={closeTratamento}
                      className="btn-secondary"
                      disabled={updating}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={updating}
                    >
                      {updating ? '⏳ Atualizando...' : '✅ Atualizar Status'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Histórico de Vendas Anteriores */}
        {vendaParaMostrarHistorico && (
          <div className="modal-overlay" onClick={fecharHistoricoVenda}>
            <div className="modal-content historico-vendas-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📋 Histórico de Vendas Anteriores</h2>
                <button onClick={fecharHistoricoVenda} className="btn-close">✕</button>
              </div>
              
              <div className="modal-body">
                {buscandoVendasAnteriores ? (
                  <div className="busca-contatos-loading">
                    Buscando vendas anteriores com os mesmos contatos...
                  </div>
                ) : (
                  <>
                    <p style={{ color: 'var(--texto-primario)', marginBottom: '1rem' }}>
                      Vendas com o mesmo <strong>telefone</strong>, <strong>CPF</strong> ou <strong>email</strong> encontradas:
                    </p>
                    
                    {vendasAnteriores && Object.keys(vendasAnteriores).length > 0 ? (
                      <div className="historico-vendas-lista">
                        {Object.entries(vendasAnteriores).map(([tipo, vendas]) => {
                          if (!vendas || vendas.length === 0) return null;
                          
                          return (
                            <div key={tipo} style={{ marginBottom: '1.5rem' }}>
                              <h4 style={{ 
                                margin: '0 0 0.5rem 0', 
                                fontSize: '1rem', 
                                color: '#dc3545',
                                textTransform: 'uppercase',
                                borderBottom: '2px solid #dc3545',
                                paddingBottom: '0.5rem'
                              }}>
                                {tipo === 'telefone' ? '📱 Telefones Repetidos' : 
                                 tipo === 'cpf' ? '🆔 CPFs Repetidos' : '📧 Emails Repetidos'}
                                <span style={{ 
                                  marginLeft: '0.5rem', 
                                  background: '#dc3545', 
                                  color: 'white', 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '10px',
                                  fontSize: '0.8rem'
                                }}>
                                  {vendas.length}
                                </span>
                              </h4>
                              
                              {vendas.map((vendaAnterior) => (
                                <div 
                                  key={vendaAnterior.id} 
                                  className="historico-venda-item"
                                  onClick={() => {
                                    abrirModalDetalhesVenda(vendaAnterior);
                                  }}
                                >
                                  <div className="historico-venda-item-header">
                                    <span className="historico-venda-id">#{vendaAnterior.id}</span>
                                    <span className={`historico-venda-status ${getStatusClass(vendaAnterior.status)}`}>
                                      {getStatusText(vendaAnterior.status)}
                                    </span>
                                  </div>
                                  <div className="historico-venda-info">
                                    <p style={{ margin: 0 }}>
                                      <strong>Cliente:</strong> {vendaAnterior.nome_cliente}
                                    </p>
                                    <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                      {vendaAnterior.telefone && <span>📞 {formatPhone(vendaAnterior.telefone)} • </span>}
                                      {vendaAnterior.cpf && <span>🆔 {formatCPF(vendaAnterior.cpf)} • </span>}
                                      {vendaAnterior.email && <span>📧 {vendaAnterior.email}</span>}
                                    </p>
                                    <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                      <strong>Data:</strong> {vendaAnterior.created_at ? formatDateOnly(vendaAnterior.created_at) : '-'}
                                    </p>
                                    {vendaAnterior.tratador && (
                                      <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                        <strong>Tratador:</strong> {vendaAnterior.tratador.nome}
                                      </p>
                                    )}
                                  </div>
                                  <div className="historico-venda-vendedor">
                                    <strong>Vendedor:</strong> {vendaAnterior.vendedor?.nome || '-'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--texto-secundario)' }}>
                        <p>Nenhuma venda anterior encontrada com os mesmos contatos.</p>
                      </div>
                    )}
                  </>
                )}

                {/* Modal de Detalhes da Venda (usado do histórico de vendas anteriores) */}
                {vendaSelecionadaParaModal && (
                  <div className="modal-overlay" onClick={fecharModalDetalhes} style={{ zIndex: 1002 }}>
                    <div className="modal-content-venda" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header-venda" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <h2 style={{ margin: 0 }}>👁️ Detalhes da Venda #{vendaSelecionadaParaModal.id}</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {['admin', 'diretor'].includes(user?.role) && (
                            <button
                              type="button"
                              className="btn-secondary"
                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                              onClick={abrirHistVisualizacaoFila}
                            >
                              Hist. visualização
                            </button>
                          )}
                          <span className="close-modal" onClick={fecharModalDetalhes}>×</span>
                        </div>
                      </div>
                      
                      <div className="modal-body-venda">
                        {/* Informações do Cliente */}
                        <div className="info-section">
                          <h4>👤 Informações do Cliente</h4>
                          <div className="info-grid">
                            <div className="info-item">
                              <div className="info-label">Nome/Razão Social</div>
                              <div className="info-value">
                                {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' 
                                  ? (vendaSelecionadaParaModal.razao_social || vendaSelecionadaParaModal.nome_cliente)
                                  : vendaSelecionadaParaModal.nome_cliente
                                }
                              </div>
                            </div>
                            {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' && vendaSelecionadaParaModal.nome_cliente && (
                              <div className="info-item">
                                <div className="info-label">Nome do Cliente</div>
                                <div className="info-value">{vendaSelecionadaParaModal.nome_cliente}</div>
                              </div>
                            )}
                            <div className="info-item">
                              <div className="info-label">Tipo</div>
                              <div className="info-value">
                                {vendaSelecionadaParaModal.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}
                              </div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Documento</div>
                              <div className="info-value">{formatDocumento(vendaSelecionadaParaModal.documento)}</div>
                            </div>
                            {vendaSelecionadaParaModal.cpf && (
                              <div className="info-item">
                                <div className="info-label">CPF</div>
                                <div className="info-value">{formatDocumento(vendaSelecionadaParaModal.cpf)}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.nome_representante && (
                              <div className="info-item">
                                <div className="info-label">Nome do Representante</div>
                                <div className="info-value">{vendaSelecionadaParaModal.nome_representante}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.rg && (
                              <div className="info-item">
                                <div className="info-label">RG</div>
                                <div className="info-value">{vendaSelecionadaParaModal.rg}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.nome_mae && (
                              <div className="info-item">
                                <div className="info-label">Nome da Mãe</div>
                                <div className="info-value">{vendaSelecionadaParaModal.nome_mae}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.data_nascimento && (
                              <div className="info-item">
                                <div className="info-label">Data de Nascimento</div>
                                <div className="info-value">{formatDateOnly(vendaSelecionadaParaModal.data_nascimento)}</div>
                              </div>
                            )}
                            <div className="info-item">
                              <div className="info-label">Telefone</div>
                              <div className="info-value">{formatPhone(vendaSelecionadaParaModal.telefone)}</div>
                            </div>
                            {vendaSelecionadaParaModal.telefone2 && (
                              <div className="info-item">
                                <div className="info-label">Telefone 2</div>
                                <div className="info-value">{formatPhone(vendaSelecionadaParaModal.telefone2)}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.email && (
                              <div className="info-item">
                                <div className="info-label">Email</div>
                                <div className="info-value">{vendaSelecionadaParaModal.email}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Endereço */}
                        <div className="info-section">
                          <h4>📍 Endereço</h4>
                          <div className="info-grid">
                            <div className="info-item">
                              <div className="info-label">CEP</div>
                              <div className="info-value">{vendaSelecionadaParaModal.cep}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Logradouro</div>
                              <div className="info-value">{vendaSelecionadaParaModal.logradouro}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Número</div>
                              <div className="info-value">{vendaSelecionadaParaModal.numero}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Bairro</div>
                              <div className="info-value">{vendaSelecionadaParaModal.bairro}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Cidade</div>
                              <div className="info-value">{vendaSelecionadaParaModal.cidade}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Estado</div>
                              <div className="info-value">
                                {vendaSelecionadaParaModal.estado_completo} ({vendaSelecionadaParaModal.estado})
                              </div>
                            </div>
                            {vendaSelecionadaParaModal.complemento1 && (
                              <div className="info-item">
                                <div className="info-label">Complemento 1</div>
                                <div className="info-value">{vendaSelecionadaParaModal.complemento1}</div>
                              </div>
                            )}
                            {vendaSelecionadaParaModal.complemento2 && (
                              <div className="info-item">
                                <div className="info-label">Complemento 2</div>
                                <div className="info-value">{vendaSelecionadaParaModal.complemento2}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Agendamento de Instalação */}
                        <div className="info-section">
                          <h4>📅 Agendamento de Instalação</h4>
                          <div className="info-grid">
                            <div className="info-item">
                              <div className="info-label">Data Opção 1</div>
                              <div className="info-value">
                                {formatDateOnly(vendaSelecionadaParaModal.data_agendamento1)} - 
                                {vendaSelecionadaParaModal.periodo_agendamento1?.toUpperCase()}
                              </div>
                            </div>
                            {vendaSelecionadaParaModal.data_agendamento2 && (
                              <div className="info-item">
                                <div className="info-label">Data Opção 2</div>
                                <div className="info-value">
                                  {formatDateOnly(vendaSelecionadaParaModal.data_agendamento2)} - 
                                  {vendaSelecionadaParaModal.periodo_agendamento2?.toUpperCase()}
                                </div>
                              </div>
                            )}
                            <div className="info-item">
                              <div className="info-label">Aceita Antecipação</div>
                              <div className="info-value">
                                {vendaSelecionadaParaModal.aceita_antecipacao ? 'Sim' : 'Não'}
                              </div>
                            </div>
                            {vendaSelecionadaParaModal.observacao_instalacao && (
                              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                <div className="info-label">Observação sobre Instalação</div>
                                <div className="info-value">{vendaSelecionadaParaModal.observacao_instalacao}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Informações Financeiras */}
                        <div className="info-section">
                          <h4>💰 Informações Financeiras</h4>
                          <div className="info-grid">
                            <div className="info-item">
                              <div className="info-label">Valor Total</div>
                              <div className="info-value" style={{ color: 'var(--azul-dvs)', fontWeight: '600', fontSize: '1.125rem' }}>
                                R$ {parseFloat(vendaSelecionadaParaModal.valor_total || 0).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Data de Vencimento</div>
                              <div className="info-value">Dia {vendaSelecionadaParaModal.data_vencimento}</div>
                            </div>
                          </div>
                        </div>

                        {/* Observações */}
                        {vendaSelecionadaParaModal.observacao_venda && (
                          <div className="info-section">
                            <h4>📝 Observações da Venda</h4>
                            <div className="observacao-venda-value">
                              {vendaSelecionadaParaModal.observacao_venda}
                            </div>
                          </div>
                        )}

                        {/* Status e Informações */}
                        <div className="info-section">
                          <h4>ℹ️ Status e Informações</h4>
                          <div className="info-grid">
                            <div className="info-item">
                              <div className="info-label">Status</div>
                              <div className="info-value">
                                <span className={`status-badge ${getStatusClass(vendaSelecionadaParaModal.status)}`}>
                                  {getStatusText(vendaSelecionadaParaModal.status)}
                                </span>
                              </div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Vendedor</div>
                              <div className="info-value">{vendaSelecionadaParaModal.vendedor?.nome || '-'}</div>
                            </div>
                            {vendaSelecionadaParaModal.tratador && (
                              <div className="info-item">
                                <div className="info-label">Tratador</div>
                                <div className="info-value">{vendaSelecionadaParaModal.tratador.nome}</div>
                              </div>
                            )}
                            <div className="info-item">
                              <div className="info-label">Data de Cadastro</div>
                              <div className="info-value">{formatDateTime(vendaSelecionadaParaModal.created_at)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {histVizOpen && (
                      <div
                        className="modal-overlay"
                        style={{ zIndex: 10050, position: 'fixed', inset: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistVizOpen(false);
                        }}
                      >
                        <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px', maxHeight: '85vh', overflow: 'auto' }}>
                          <div className="modal-header-venda">
                            <h2>Histórico de visualização — Venda #{vendaSelecionadaParaModal.id}</h2>
                            <span className="close-modal" onClick={() => setHistVizOpen(false)}>×</span>
                          </div>
                          <div className="modal-body-venda">
                            {histVizLoading ? (
                              <p>Carregando…</p>
                            ) : histVizItems.length === 0 ? (
                              <p style={{ color: 'var(--texto-secundario)' }}>Nenhum registro.</p>
                            ) : (
                              <table className="fila-table" style={{ width: '100%', fontSize: '0.9rem' }}>
                                <thead>
                                  <tr>
                                    <th>Usuário</th>
                                    <th>Data/hora</th>
                                    <th>Origem</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {histVizItems.map((row) => (
                                    <tr key={row.id}>
                                      <td>{row.usuario?.nome || row.usuario?.username || `#${row.user_id}`}</td>
                                      <td>{formatDateTime(row.viewed_at)}</td>
                                      <td>{ORIGEM_VISUALIZACAO_LABEL[row.source_page] || row.source_page}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FilaTratamento;
